import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";

import {
  getThemeClass,
  getThemeComponentClasses,
} from "@/src_new/shared/lib/themeComponentClasses";

type CommonPopupProps = {
  isOpen: boolean;
  name?: string | null;
  title?: string;
  children?: React.ReactNode;
  header?: React.ReactNode;
  className?: string;
  outerClassName?: string;
  bodyClassName?: string;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  onClose: () => void;
};

export default function CommonPopup({
  isOpen,
  name,
  title,
  children,
  header,
  className = "min-w-[300px]",
  outerClassName = "items-center",
  bodyClassName = "",
  closeOnOverlayClick = true,
  showCloseButton = true,
  onClose,
}: CommonPopupProps) {
  const modalClasses = getThemeComponentClasses().modal;

  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      id={`common-popup-${name || "active"}`}
      className="fixed inset-0 z-50"
      data-popup-name={name || undefined}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`absolute inset-0 backdrop-blur-sm ${getThemeClass(
          modalClasses.overlayBgColor,
          "bg-theme-text/10"
        )}`}
        onClick={closeOnOverlayClick ? onClose : undefined}
      />

      <div className={`relative flex min-h-screen justify-center ${outerClassName}`}>
        <div
          className={` rounded-xl  shadow-xl animate-in fade-in zoom-in-95 ${getThemeClass(
            modalClasses.panelBgColor,
            "bg-theme-surface"
          )} ${getThemeClass(modalClasses.panelBorderColor, "")} ${className}`}
        >
          {title || header || showCloseButton ? (
            <div className="flex items-center justify-between py-3 px-5">
              <h3
                className={`font-semibold ${getThemeClass(
                  modalClasses.titleColor || modalClasses.panelTextColor,
                  "text-theme-text"
                )}`}
              >
                {title}
              </h3>
              <div className="flex items-center gap-3">
                {header}
                {showCloseButton ? (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close popup"
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${getThemeClass(
                      modalClasses.closeButtonColor,
                      "text-theme-text-soft hover:text-theme-text-muted"
                    )} hover:bg-theme-surface-soft`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <div
            className={`p-3 ${getThemeClass(
              modalClasses.bodyTextColor || modalClasses.panelTextColor,
              "text-theme-text-muted"
            )} ${bodyClassName}`}
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
